/* Aria Robotics — Nestor 01 Produktseite. Ported aus page-product.jsx. */
import { Container, Section, SectionHead, Eyebrow, Btn, Chip, Panel, IconTile, Icon, ImageFrame, ImageSlot, go } from '@/ui';
import { AriaData as D } from '@/data';

export function Product() {
  return (
    <div>
      {/* Produkt-Hero */}
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(700px 420px at 20% 10%, rgba(74,144,226,0.26), transparent 60%)' }}>
        <Container>
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center', padding: '84px 0' }}>
            <div>
              <Chip tone="inverse" dot>Produkt · Erste Generation</Chip>
              <h1 style={{ fontSize: 'clamp(38px, 5.5vw, 60px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.02, margin: '20px 0 0' }}>Nestor 01</h1>
              <p style={{ fontSize: 20, fontWeight: 300, color: 'rgba(255,255,255,0.82)', marginTop: 16, maxWidth: '42ch', lineHeight: 1.55 }}>
                Der Erste, der wirklich hilft. Ein humanoider Haushaltshelfer, der mit anpackt — ruhig, verlässlich und ehrlich über seine Grenzen.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 30 }}>
                <Btn variant="cta" size="lg" iconRight="arrow-right" onClick={() => go('/kontakt')}>Vorbestellen</Btn>
                <Btn variant="ghostInverse" size="lg" iconLeft="file-text" href="#/service">Service ansehen</Btn>
              </div>
            </div>
            <div style={{ position: 'relative', aspectRatio: '3/4', borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
              <ImageSlot id="product-hero" shape="rect" src="assets/nestor/giessen.webp" placeholder="Nestor 01" style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        </Container>
      </header>

      {/* Fähigkeiten */}
      <Section tone="page">
        <Container>
          <SectionHead eyebrow="Was Nestor übernimmt" title="Hilfe, die man im Alltag merkt" lead="Nestor löst keine Show-Aufgaben, sondern die kleinen, wiederkehrenden Dinge, die zusammen so viel Zeit kosten." />
          <div className="ar-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginTop: 44 }}>
            {D.capabilities.map((c) => (
              <Panel key={c.h} hover>
                <IconTile name={c.icon} />
                <h3 style={{ fontSize: 18, color: 'var(--text-100)', margin: '16px 0 8px' }}>{c.h}</h3>
                <p style={{ fontSize: 14.5, color: 'var(--text-200)', lineHeight: 1.6, margin: 0 }}>{c.p}</p>
              </Panel>
            ))}
          </div>
        </Container>
      </Section>

      {/* Foto-Galerie */}
      <Section tone="sunken">
        <Container>
          <SectionHead eyebrow="Nestor im Einsatz" title="Ein Tag mit Nestor 01" lead="Drinnen wie draußen: echte Aufgaben, ruhig und verlässlich erledigt." />
          <div className="ar-gallery" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 44 }}>
            {D.gallery.map((g) => {
              const slug = (g.img.split('/').pop() ?? '').replace('.webp', '');
              return (
                <Panel key={g.h} variant="default" padding={0} hover style={{ overflow: 'hidden' }}>
                  <div style={{ position: 'relative', aspectRatio: '16/10' }}>
                    <ImageSlot id={`gal-${slug}`} shape="rect" src={g.img} placeholder={g.h} style={{ width: '100%', height: '100%' }} />
                    <span style={{ position: 'absolute', left: 12, top: 12 }}><Chip tone="inverse">{g.room}</Chip></span>
                  </div>
                  <div style={{ padding: 20 }}>
                    <h3 style={{ fontSize: 17, color: 'var(--text-100)', margin: '0 0 6px' }}>{g.h}</h3>
                    <p style={{ fontSize: 14, color: 'var(--text-200)', lineHeight: 1.55, margin: 0 }}>{g.p}</p>
                  </div>
                </Panel>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Ehrlich über Grenzen */}
      <Section tone="sunken">
        <Container>
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: 56, alignItems: 'center' }}>
            <div>
              <Eyebrow>Ehrlichkeit gehört dazu</Eyebrow>
              <h2 style={{ fontSize: 'clamp(26px, 3.6vw, 36px)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-100)', margin: '14px 0 0', lineHeight: 1.1 }}>Was Nestor (noch) nicht kann</h2>
              <p style={{ fontSize: 17, color: 'var(--text-200)', lineHeight: 1.6, marginTop: 14 }}>
                Wir versprechen lieber weniger und halten es. Deshalb sagen wir klar, wo Nestor heute an Grenzen stößt.
              </p>
            </div>
            <Panel variant="default" padding={8} style={{ background: 'var(--surface-card)' }}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {D.limits.map((l, i) => (
                  <li key={l} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '16px 18px', borderBottom: i < D.limits.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                    <span style={{ color: 'var(--accent-200)', marginTop: 1, flex: 'none' }}><Icon name="info" size={20} /></span>
                    <span style={{ fontSize: 15.5, color: 'var(--text-100)', lineHeight: 1.5 }}>{l}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </Container>
      </Section>

      {/* Specs */}
      <Section tone="page">
        <Container>
          <SectionHead eyebrow="Technische Daten" title="Nestor 01 im Detail" lead="Nur Zahlen, die Ihnen im Alltag etwas sagen." />
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginTop: 40, alignItems: 'start' }}>
            <Panel variant="flat" padding={8}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {D.specs.map((s, i) => (
                    <tr key={s.k} style={{ borderBottom: i < D.specs.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <td style={{ padding: '14px 18px', fontSize: 14.5, color: 'var(--text-200)', width: '42%' }}>{s.k}</td>
                      <td style={{ padding: '14px 18px', fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--text-100)', letterSpacing: '0.01em' }}>{s.v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <ImageFrame id="product-detail" ratio="16/9" src="assets/nestor/ansichten.webp" placeholder="Nestor 01 von allen Seiten" />
              <p style={{ fontSize: 13, color: 'var(--text-200)', margin: '-8px 2px 0', textAlign: 'center' }}>Nestor 01 — Vorder-, Seiten- und Rückansicht</p>
              <Panel variant="brand">
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--accent-100)', flex: 'none' }}><Icon name="lock" size={24} /></span>
                  <div>
                    <h3 style={{ fontSize: 18, color: '#fff', margin: '0 0 6px' }}>Ihre Daten bleiben zuhause</h3>
                    <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, margin: 0 }}>Nestor verarbeitet Bild und Ton lokal. Die Kamera lässt sich jederzeit mechanisch abdecken. Nichts verlässt Ihr Zuhause ohne Ihre Zustimmung.</p>
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </Container>
      </Section>

      {/* Preis / CTA */}
      <Section tone="ink" style={{ padding: '80px 0' }}>
        <Container>
          <div style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
            <Eyebrow tone="inverse">Erste Auslieferungswelle 2026</Eyebrow>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', margin: '14px 0 0' }}>Nestor 01 vorbestellen</h2>
            <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.78)', marginTop: 14 }}>Unverbindlich reservieren, ohne Vorkasse. Sie entscheiden nach dem persönlichen Gespräch.</p>
            <div style={{ marginTop: 28 }}><Btn variant="cta" size="lg" iconRight="arrow-right" onClick={() => go('/kontakt')}>Platz sichern</Btn></div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
